import { EKnowledgeProcessingStatus } from "@frontend-shared/types";
import { getKnowledgeProcessingErrorI18nKey } from "@frontend-shared/utils";
import { AlertCircle } from "lucide-react";
import React, { memo } from "react";
import { useTranslation } from "react-i18next";

import { HoverTapTooltip } from "@/components/HoverTapTooltip";

import { KnowledgeStatusBadge } from "./KnowledgeStatusBadge";
import type { IKnowledgeFailedStatusBadgeProps } from "./types";

// NOTE: NSAI-7817 — the translated reason a file failed to index ("too much
// text — split it", "took too long", ...). NSAI-7345 — shown in the Failed
// badge's tooltip (hover, keyboard focus or tap), as the "Little text" chip
// shows its sentence, instead of as a paragraph under the badge. A failure with
// no reason at all keeps the plain badge.
const KnowledgeFailedStatusBadgeInner: React.FC<
  IKnowledgeFailedStatusBadgeProps
> = ({ code, rawError, testId }) => {
  // NOTE: The instance's `t` for the reason — its keys carry their namespace
  // ("knowledge:…"), which the hook's namespace-bound `t` does not accept.
  const { t, i18n } = useTranslation("knowledge");
  const key = getKnowledgeProcessingErrorI18nKey({ code });
  const reason = key ? i18n.t(key) : rawError;

  if (!reason) {
    return <KnowledgeStatusBadge status={EKnowledgeProcessingStatus.FAILED} />;
  }

  return (
    <HoverTapTooltip
      label={t("status.failed")}
      content={reason}
      testId={testId}
    >
      <KnowledgeStatusBadge
        status={EKnowledgeProcessingStatus.FAILED}
        icon={<AlertCircle className="size-3 shrink-0" aria-hidden="true" />}
        className="cursor-help"
      />
    </HoverTapTooltip>
  );
};

export const KnowledgeFailedStatusBadge = memo(KnowledgeFailedStatusBadgeInner);
